import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { BuilderQuiz, Question, CreateQuizInput, CreateQuestionInput } from '@/types';
import { KEYS } from './course-builder.keys';

// ── Quizzes ──

export function useQuiz(quizId: number) {
  return useQuery({
    queryKey: KEYS.quiz(quizId),
    queryFn: () => api.get<BuilderQuiz>(`/quizzes/${quizId}`),
    select: (res) => res.data,
    enabled: quizId > 0,
  });
}

export function useCreateQuiz() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { lessonId: number; data: CreateQuizInput; courseId: number }) =>
      api.post<BuilderQuiz>(`/lessons/${vars.lessonId}/quiz`, vars.data),
    onSuccess: (_, v) => qc.invalidateQueries({ queryKey: KEYS.detail(v.courseId) }),
  });
}

export function useDeleteQuiz() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { quizId: number; courseId: number }) =>
      api.delete<void>(`/quizzes/${vars.quizId}`),
    onSuccess: (_, v) => qc.invalidateQueries({ queryKey: KEYS.detail(v.courseId) }),
  });
}

// ── Questions ──

export function useCreateQuestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { quizId: number; data: CreateQuestionInput; courseId: number }) =>
      api.post<Question>(`/quizzes/${vars.quizId}/questions`, vars.data),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: KEYS.quiz(v.quizId) });
      qc.invalidateQueries({ queryKey: KEYS.detail(v.courseId) });
    },
  });
}

export function useDeleteQuestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { questionId: number; quizId: number; courseId: number }) =>
      api.delete<void>(`/questions/${vars.questionId}`),
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: KEYS.quiz(v.quizId) });
      qc.invalidateQueries({ queryKey: KEYS.detail(v.courseId) });
    },
  });
}
